import React, { useState, useEffect } from "react";
import {mobile} from "../services/mobileBridge";
import {ShowTime} from "../TVGuide";

export function ProgrammeModal({
                                   data,
                                   showTime,
                                   onClose,
                                   country = "south-africa",
                               }: {
    data: any;
    showTime?: ShowTime | null;
    onClose: () => void;
    country?: "south-africa" | "nigeria";
}) {
    // always render, even if data is undefined
    const hiddenItems = [
        "GlobalID",
        "GlobalSeasonID",
        "GlobalSeriesID",
        "Source Key ID",
    ];
    const keyWords: { Name: string; Value: string }[] = data?.Keywords || [];

    const [playPuzzle, setPlayPuzzle] = useState(false);
    const [actionMessage, setActionMessage] = useState<string | null>(null);

    useEffect(() => {
        return mobile.onNativeBack(onClose);
    }, [onClose]);

    const handleShare = async () => {
        const text = showTime
            ? `${data?.Title} — ${showTime.channelName} (Channel ${showTime.channelNumber}), ${showTime.date} ${showTime.startTime}-${showTime.endTime}`
            : `${data?.Title} on ${data?.Channel}`;
        const shareUrl = showTime
            ? `${window.location.origin}/programme/${country}/${showTime.uniqueEventId}`
            : window.location.href;
        const shared = await mobile.share({
            title: data?.Title,
            text,
            url: shareUrl,
        });
        if (!shared) {
            setActionMessage("Sharing is unavailable on this device.");
        }
    };

    const handleAddToCalendar = async () => {
        if (!showTime) return;
        const toDateTime = (time: string) => {
            const parts = time.split(":");
            const normalized = parts.length === 2 ? `${time}:00` : time;
            return new Date(`${showTime.dateYYYMMDD}T${normalized}`);
        };
        const start = toDateTime(showTime.startTime);
        const end = toDateTime(showTime.endTime);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            setActionMessage("Unable to add the event to a calendar on this device.");
            return;
        }
        if (end.getTime() <= start.getTime()) {
            end.setDate(end.getDate() + 1);
        }
        const added = await mobile.addCalendarEvent({
            title: data?.Title || "TV programme",
            start: start.toISOString(),
            end: end.toISOString(),
            description: `${showTime.channelName} - Channel ${showTime.channelNumber}`,
            location: showTime.channelName,
            id: showTime.uniqueEventId,
        });
        if (!added) {
            setActionMessage("Unable to add the event to a calendar on this device.");
        }
    };

    return (
        <div className="modal-backdrop">
            <div className="modal text-theme-color">
                <i className="modal-close-btn fas fa-times close-icon" onClick={onClose}></i>
                <h2>{data?.Title}</h2>
                <p><strong>Duration:</strong> {data?.Duration}</p>
                <p><strong>Channel:</strong> {data?.Channel}</p>
                <p><strong>Synopsis:</strong> {data?.Synopsis}</p>
                <p><strong>Rating:</strong> {data?.Rating}</p>

                {!playPuzzle ? (
                    <>
                        <img src={data?.ThumbnailUri} alt="thumbnail" style={{ width: "100%" }} />
                        <button className="modal-btn modal-btn-primary" style={{ width: "100%" }} onClick={() => setPlayPuzzle(true)}>
                            🧩 Play 15-Tile Puzzle
                        </button>
                        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                            <button className="modal-btn modal-btn-primary" style={{ margin: 0, flex: 1 }} onClick={handleShare}>
                                📤 Share
                            </button>
                            {showTime && (
                                <button className="modal-btn" style={{ margin: 0, flex: 1 }} onClick={handleAddToCalendar}>
                                    📅 Add to calendar
                                </button>
                            )}
                        </div>
                        {actionMessage && (
                            <p className="modal-action-message">{actionMessage}</p>
                        )}
                    </>
                ) : (
                    <>
                        <PuzzleGame imageUrl={data?.ThumbnailUri} size={300} />
                        <button className="modal-btn" style={{ width: "100%" }} onClick={() => setPlayPuzzle(false)}>
                            Show Original
                        </button>
                    </>
                )}

                <ul>
                    {data?.Items?.map((item: any, index: number) => (
                        <li hidden={hiddenItems.includes(item.Key)} key={index}>
                            <strong>{item.Key}:</strong> {item.Value}
                        </li>
                    ))}
                </ul>

                <div>
                    {keyWords
                        .filter((item) => item.Value)
                        .map((word, index) => (
                            <div key={index} className="keyword-pill">
                                {word.Value}
                            </div>
                        ))}
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------
// 3. PuzzleGame component
// ------------------------------------------------------------------
type PuzzleGameProps = {
    imageUrl: string;
    size: number; // px, width and height of whole puzzle
};

function PuzzleGame({ imageUrl, size }: PuzzleGameProps) {
    const DIM = 4;
    const TILE_COUNT = DIM * DIM;
    const [tiles, setTiles] = useState<number[]>([]);

    const row = (i: number) => Math.floor(i / DIM);
    const col = (i: number) => i % DIM;

    useEffect(() => {
        const initial = Array.from({ length: TILE_COUNT }, (_, i) =>
            i === TILE_COUNT - 1 ? 0 : i + 1
        );
        // Shuffle by simulating random moves from solved (guarantees solvable)
        let state = initial.slice();
        let blankIdx = state.indexOf(0);
        let lastMove = -1;
        for (let i = 0; i < 200; i++) {
            const r = row(blankIdx), c = col(blankIdx);
            const neighbours: number[] = [];
            if (r > 0) neighbours.push(blankIdx - DIM);
            if (r < DIM - 1) neighbours.push(blankIdx + DIM);
            if (c > 0) neighbours.push(blankIdx - 1);
            if (c < DIM - 1) neighbours.push(blankIdx + 1);
            const valid = neighbours.filter(n => n !== lastMove);
            const pick = valid[Math.floor(Math.random() * valid.length)];
            [state[blankIdx], state[pick]] = [state[pick], state[blankIdx]];
            lastMove = blankIdx;
            blankIdx = pick;
        }
        setTiles(state);
    }, [TILE_COUNT]);

    const handleClick = (idx: number) => {
        const blankIdx = tiles.indexOf(0);
        const r1 = row(idx), c1 = col(idx);
        const r0 = row(blankIdx), c0 = col(blankIdx);
        if ((r1 === r0 && Math.abs(c1 - c0) === 1) || (c1 === c0 && Math.abs(r1 - r0) === 1)) {
            const newTiles = tiles.slice();
            [newTiles[idx], newTiles[blankIdx]] = [newTiles[blankIdx], newTiles[idx]];
            setTiles(newTiles);
        }
    };

    const tileSize = size / DIM;
    const imgSrc = imageUrl ? imageUrl.replace(/^http:\/\//, "https://") : "";

    return (
        <div
            style={{
                position: "relative",
                width: size,
                height: size,
                margin: "0 auto",
                border: "2px solid var(--border-strong)",
                borderRadius: "var(--r-sm)",
                overflow: "hidden",
            }}
        >
            {tiles.map((num, idx) => {
                if (num === 0) return null;
                const r = row(idx), c = col(idx);
                const backgroundX = ((num - 1) % DIM) * tileSize;
                const backgroundY = Math.floor((num - 1) / DIM) * tileSize;

                return (
                    <div
                        key={idx}
                        onClick={() => handleClick(idx)}
                        style={{
                            position: "absolute",
                            top: r * tileSize,
                            left: c * tileSize,
                            width: tileSize,
                            height: tileSize,
                            boxSizing: "border-box",
                            border: "1px solid var(--border-strong)",
                            backgroundColor: `hsl(${(num * 47) % 360}, 50%, 24%)`,
                            backgroundImage: imgSrc ? `url("${imgSrc}")` : undefined,
                            backgroundSize: `${size}px ${size}px`,
                            backgroundPosition: `-${backgroundX}px -${backgroundY}px`,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "var(--text-3)",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                        }}
                    >
                        {!imgSrc && num}
                    </div>
                );
            })}
        </div>
    );
}
